// File: frontend/src/contexts/ThemeContext.js
// Tema FIJO de la marca Pandemonium ("El Inframundo"). No hay light/dark ni personalización:
// la identidad visual es única y no configurable por el usuario (igual que el portal y las demás
// apps de la familia). Este módulo solo ancla las clases/atributos que los estilos esperan en
// <html> y deja los colores a index.css + tailwind.config.js.
//
// Antes esto gestionaba modo light/dark/system, presets de acento, densidad y superficie
// (Settings → Apariencia). Todo eso se eliminó al adoptar el tema fijo; se conserva `ThemeProvider`
// y `useTheme` para no romper imports existentes.
import React, { createContext, useContext, useEffect } from 'react';

// Se aplica una sola vez: la clase `.dark` (mantiene vivas las ~1860 variantes `dark:` de Tailwind
// y todos los selectores `.dark`), densidad cómoda y superficie sólida.
const applyFixedTheme = () => {
  const root = document.documentElement;
  root.classList.add('dark');
  root.setAttribute('data-density', 'comfortable');
  root.setAttribute('data-surface', 'solid');
  // Limpia cualquier override de acento que una versión anterior hubiera dejado inline en <html>,
  // para que gane el carmín definido en index.css (:root).
  root.style.removeProperty('--accent');
  root.style.removeProperty('--accent-hover');
};

// Aplica de inmediato, antes del primer render, para que no haya flash de tema claro.
if (typeof document !== 'undefined') applyFixedTheme();

// Limpieza de preferencias viejas persistidas (modo claro guardado, etc.): ya no aplican.
try {
  localStorage.removeItem('ghost-appearance');
  localStorage.removeItem('darkMode');
} catch (e) { /* storage no disponible */ }

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  useEffect(() => { applyFixedTheme(); }, []);
  return (
    <ThemeContext.Provider value={{ darkMode: true, themeMode: 'dark' }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};
